import React from "react";
import { useForm, Control, FieldValues } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import TimesIcon from "@assets/img/times-icon.svg";
import { theme } from "@src/theme";
import {
  CloseDrawerButton,
  SubmitDrawerButton,
  TopButtonsGroup,
} from "@src/components/ContentDrawer/DrawerComponents";
import Tooltip from "@src/components/Tooltip";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import { RootState } from "@src/redux/store";
import {
  MoodTrackLibraryFilters,
  moodTrackLibraryFiltersInitialState,
  setTrackLibraryFilters,
  resetTrackLibraryFilters,
} from "@src/redux/slices/moodFactorySlice";
import TracksFilterFields from "@src/components/TracksFilterFields/TracksFilterFields";

const schema = yup
  .object({
    title: yup.string().optional(),
    artist: yup.string().optional(),
    album: yup.string().optional(),
    label: yup.string().optional(),
    bpm: yup.number().optional(),
    key: yup.string().optional(),
    durationInMinutes: yup.number().optional(),
    isTopLikedThisWeek: yup.boolean().optional(),
    missingYoutube: yup.boolean().optional(),
    missingSpotify: yup.boolean().optional(),
    missingApple: yup.boolean().optional(),
    missingShazam: yup.boolean().optional(),
    createdDateRange: yup
      .object()
      .shape({
        startDate: yup.string().optional(),
        endDate: yup.string().optional(),
      })
      .optional()
      .nullable(),
  })
  .required();

type MoodTracksFilterFormProps = {
  closeButtonHandler: () => void;
  role?: string;
};

const MoodTracksFilterForm: React.FC<MoodTracksFilterFormProps> = ({
  closeButtonHandler,
  role,
}) => {
  const dispatch = useAppDispatch();
  const { trackLibraryFilters } = useAppSelector(
    (state: RootState) => state.moodFactory
  );
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: trackLibraryFilters || moodTrackLibraryFiltersInitialState,
  });

  const hasActiveFilters = React.useMemo(() => {
    if (!trackLibraryFilters) return false;
    return (
      trackLibraryFilters.title !== "" ||
      trackLibraryFilters.artist !== "" ||
      trackLibraryFilters.album !== "" ||
      trackLibraryFilters.label !== "" ||
      trackLibraryFilters.bpm !== 0 ||
      trackLibraryFilters.key !== "" ||
      trackLibraryFilters.durationInMinutes !== 0 ||
      trackLibraryFilters.isTopLikedThisWeek !== false ||
      trackLibraryFilters.missingYoutube !== false ||
      trackLibraryFilters.missingSpotify !== false ||
      trackLibraryFilters.missingApple !== false ||
      trackLibraryFilters.missingShazam !== false ||
      trackLibraryFilters.createdDateRange !== undefined
    );
  }, [trackLibraryFilters]);

  const onSubmit = (data: yup.InferType<typeof schema>): void => {
    dispatch(setTrackLibraryFilters(data as MoodTrackLibraryFilters));
  };

  const resetFiltersCb = (): void => {
    reset({ ...moodTrackLibraryFiltersInitialState });
    dispatch(resetTrackLibraryFilters());
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <TopButtonsGroup>
        <CloseDrawerButton type="button" onClick={closeButtonHandler}>
          <img
            src={TimesIcon}
            alt="X"
            style={{ width: "10px", height: "10px" }}
          />
        </CloseDrawerButton>
        <div style={{ display: "flex", gap: theme.margins.medium }}>
          {(isDirty || hasActiveFilters) && (
            <Tooltip content={"Clear filters"} position="top">
              <SubmitDrawerButton type="button" onClick={resetFiltersCb}>
                Reset filters
              </SubmitDrawerButton>
            </Tooltip>
          )}
          <Tooltip content={"Submit filters"} position="top">
            <SubmitDrawerButton disabled={!isDirty}>
              Save filters
            </SubmitDrawerButton>
          </Tooltip>
        </div>
      </TopButtonsGroup>

      <TracksFilterFields
        control={control as unknown as Control<FieldValues>}
        errors={errors}
        role={role}
      />
    </form>
  );
};

export default MoodTracksFilterForm;
